import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../features/login/services/authService';
import { getRedirectPath } from '../configs/navigationConfig';
import apiService from '../services/apiService';
import { useQueryClient } from '@tanstack/react-query';

// AES-256-GCM key — must match casecrumbs-website /api/demo-login
const ENC_KEY_HEX = 'a4f8e2c1b9d3f7a0e5c8b2d6f4a9e1c3b7d0f5a2e8c4b1d9f6a3e7c0b5d2f8a1';

async function decryptSsoToken(blob: string): Promise<string | null> {
  try {
    // base64url → standard base64 with padding (atob requires proper padding)
    const b64 = blob.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - b64.length % 4) % 4);
    const raw = Uint8Array.from(atob(padded), c => c.charCodeAt(0));
    const iv  = raw.slice(0, 12);
    const tag = raw.slice(12, 28);
    const enc = raw.slice(28);
    const keyBytes = new Uint8Array(ENC_KEY_HEX.match(/.{2}/g)!.map(b => parseInt(b, 16)));
    const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, ['decrypt']);
    // WebCrypto AES-GCM expects ciphertext followed by auth tag
    const ciphertextWithTag = new Uint8Array(enc.length + tag.length);
    ciphertextWithTag.set(enc, 0);
    ciphertextWithTag.set(tag, enc.length);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv, tagLength: 128 }, key, ciphertextWithTag);
    return new TextDecoder().decode(decrypted);
  } catch {
    return null;
  }
}

interface User {
  id: string;
  email?: string;
  phone?: string;
  name: string;
  role: string;
  status: string;
  avatar?: string;
  profile?: any;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (userData: User, accessToken: string) => void;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const isTokenExpired = (accessToken: string): boolean => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
    queryClient.clear();

    setIsAuthenticated(false);
    setUser(null);
    setError(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  const redirectUser = useCallback(
    (user: User, shouldRespectFromPath: boolean = false) => {
      const redirectPath = getRedirectPath(user.status.toUpperCase());

      if (shouldRespectFromPath) {
        const from = location.state?.from?.pathname;
        const finalPath = from && !from.includes('/login') ? from : redirectPath;
        navigate(finalPath, { replace: true });
      } else {
        navigate(redirectPath, { replace: true });
      }
    },
    [navigate, location]
  );

  const login = useCallback(
    (userData: User, accessToken: string) => {
      try {
        const enhancedUser: User = {
          ...userData,
          role: userData.role,
          status: userData.status
        };

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('user', JSON.stringify(enhancedUser));

        setIsAuthenticated(true);
        setUser(enhancedUser);
        setError(null);

        redirectUser(enhancedUser, true);
      } catch (err) {
        setError('Failed to save login information');
        console.error('Login error:', err);
      }
    },
    [redirectUser]
  );

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // ------------------------------------------------------------------
        // SSO token detection (Moodle → Falco LMS 3-step flow)
        // The NestJS SSO Step-3 endpoint redirects here as:
        //   https://learn.falcoperegrinus.in/lmsv2/?sso_token=<jwt>
        // We read window.location.search (before the # hash) so this works
        // regardless of HashRouter's internal routing state.
        // ------------------------------------------------------------------
        const urlParams = new URLSearchParams(window.location.search);
        const ssoToken = urlParams.get('sso_token');
        const ssoCourseCode = urlParams.get('course_code');
        const ssoPage = urlParams.get('page');
        const ssoError = urlParams.get('sso_error');

        if (ssoError) {
          // Clean up the URL param
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname + window.location.hash,
          );
          setError('SSO login failed: ' + ssoError.replace(/_/g, ' '));
          setIsLoading(false);
          return;
        }

        if (ssoToken) {
          // Remove token from URL immediately (security: don't leave JWT in browser history)
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname + window.location.hash,
          );

          // If token has no dots it's an AES-256-GCM encrypted blob — decrypt first
          const jwt = ssoToken.includes('.')
            ? ssoToken
            : await decryptSsoToken(ssoToken);

          if (jwt && !isTokenExpired(jwt)) {
            try {
              // Decode (not verify – server already verified) to extract user fields
              const payload = JSON.parse(atob(jwt.split('.')[1])) as {
                userId: string;
                email: string;
                name?: string;
                role: string;
                schema: string;
                exp: number;
              };

              const ssoUser = {
                id: payload.userId,
                email: payload.email,
                name: payload.name || payload.email,
                role: payload.role,
                status: 'active',
              };

              localStorage.setItem('accessToken', jwt);
              localStorage.setItem('user', JSON.stringify(ssoUser));
              localStorage.setItem('studentId', payload.userId);

              setUser(ssoUser);
              setIsAuthenticated(true);
              setIsLoading(false);
              // Deep-link: if course_code was forwarded from Moodle, navigate to
              // the target page (/case-crumbs or /sustain-skills) with the filter param.
              let destination = '/dashboard';
              if (ssoCourseCode) {
                const ALLOWED_PAGES: Record<string, string> = {
                  'case-crumbs':    '/case-crumbs',
                  'sustain-skills': '/sustain-skills',
                };
                const targetPage = (ssoPage && ALLOWED_PAGES[ssoPage]) || '/case-crumbs';
                destination = `${targetPage}?course_code=${encodeURIComponent(ssoCourseCode)}`;
              }
              navigate(destination, { replace: true });
              return;
            } catch (decodeErr) {
              console.error('SSO token decode error:', decodeErr);
            }
          }
          // Token invalid/expired – fall through to normal auth check
        }

        // ------------------------------------------------------------------
        // Normal auth initialisation (existing logic, unchanged)
        // ------------------------------------------------------------------
        const accessToken = localStorage.getItem('accessToken');
        const storedUser = localStorage.getItem('user');        
        
        if (accessToken && storedUser && !isTokenExpired(accessToken)) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);

        } else {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    apiService.setLogoutCallback(logout);
  }, [logout]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return;
    
    if (location.pathname === '/login') return;
    
    const expectedPath = getRedirectPath(user.status.toUpperCase());
    
    if (location.pathname === '/' || location.pathname === '') {
      navigate(expectedPath, { replace: true });
    }
  }, [location.pathname, isAuthenticated, user, isLoading, navigate]);

  const contextValue = {
    isAuthenticated,
    user,
    login,
    logout,
    isLoading,
    error,
    clearError
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
